/**
 * 檔案處理工具函式
 */

import { IMAGE } from '@/config/constants';

// ========================================================================
// 檔案讀取
// ========================================================================

/**
 * 將檔案轉換為 Base64 字串
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Failed to read file as base64'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// ========================================================================
// 圖片處理
// ========================================================================

/**
 * 載入圖片（內部使用）
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

/**
 * 縮放圖片（保持比例）
 */
export async function resizeImage(
  file: File,
  maxWidth: number = IMAGE.MAX_WIDTH,
  quality: number = IMAGE.JPEG_QUALITY
): Promise<{ blob: Blob; dataUrl: string; width: number; height: number }> {
  // 讀取圖片
  const dataUrl = await fileToBase64(file);
  const img = await loadImage(dataUrl);

  // 計算新尺寸
  let width = img.width;
  let height = img.height;

  if (width > maxWidth) {
    const ratio = maxWidth / width;
    width = maxWidth;
    height = Math.round(height * ratio);
  }

  // 繪製到 Canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  ctx.drawImage(img, 0, 0, width, height);

  // 決定輸出格式
  const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';

  // 轉換為 Blob
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('Failed to create blob'));
      },
      mimeType,
      quality
    );
  });

  // 轉換為 Data URL
  const resizedDataUrl = canvas.toDataURL(mimeType, quality);

  return {
    blob,
    dataUrl: resizedDataUrl,
    width,
    height,
  };
}

// ========================================================================
// 檔案下載
// ========================================================================

/**
 * 下載 Blob 檔案
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

